import React, { useRef, useEffect, useMemo } from "react";
import { Container, Sprite } from "@pixi/react";
import { useSelector } from "react-redux";
import ReelBackController, {
  STATIC_CONFIG,
  STATIC_CONFIG_FEATURE,
  SPINE_CONFIG,
} from "./ReelBackController";
import { Settings } from "../../../settings";
import { safeInit } from "../../../utilities";
import { selectHoldAndWinActive } from "../../../State/selectors";
import * as PIXI from "pixi.js";
import { ASSET_NAME } from "../../../assets";

const ReelBack = ({
  symbolWidth,
  symbolHeight,
  dimensions,
  dividerDimensions,
  renderMode = "spine", // Changed default from "static" to "spine"
}) => {
  const reelBackControllerRef = useRef(null);
  const containerRef = useRef(null);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);

  useEffect(() => {
    // Only initialize controller for spine mode
    if (
      renderMode === "spine" &&
      containerRef.current &&
      !reelBackControllerRef.current
    ) {
      safeInit(() => {
        reelBackControllerRef.current = new ReelBackController(
          containerRef.current,
          symbolWidth,
          symbolHeight,
          dimensions,
          dividerDimensions,
        );
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderMode]);

  // Handle dimension changes for responsive design (spine mode only)
  useEffect(() => {
    if (renderMode === "spine" && reelBackControllerRef.current) {
      reelBackControllerRef.current.resizeReelBack(
        symbolWidth,
        symbolHeight,
        dimensions,
        dividerDimensions,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolHeight, renderMode]);

  // Memoize divider count - constant value
  const dividerCount = useMemo(() => Settings.REEL_COUNT - 1, []);

  // Memoize config selection based on Hold and Win state
  const config = useMemo(
    () => (holdAndWinActive ? STATIC_CONFIG_FEATURE : STATIC_CONFIG),
    [holdAndWinActive],
  );

  // Memoize divider width
  const dividerWidth = useMemo(
    () => dividerDimensions?.width ?? 0,
    [dividerDimensions?.width],
  );

  // Memoize static dimensions calculations
  const staticWidth = useMemo(
    () =>
      (symbolWidth * Settings.REEL_COUNT + dividerWidth * dividerCount) *
      config.widthMultiplier,
    [symbolWidth, dividerWidth, dividerCount, config.widthMultiplier],
  );

  const staticHeight = useMemo(
    () => symbolHeight * Settings.SYMBOLS_VISIBLE * config.heightMultiplier,
    [symbolHeight, config.heightMultiplier],
  );

  // Memoize texture lookup for static mode
  const staticTexture = useMemo(() => {
    if (renderMode !== "static") return null;
    return holdAndWinActive
      ? PIXI.Assets.get(ASSET_NAME.reel_frame_back_static_feature)
      : PIXI.Assets.get(ASSET_NAME.reel_frame_back_static);
  }, [renderMode, holdAndWinActive]);

  // Memoize container position calculations
  const containerPosition = useMemo(() => {
    if (renderMode === "spine") {
      return {
        x: symbolWidth * Settings.REEL_COUNT * SPINE_CONFIG.horizontalOffset,
        y:
          -symbolHeight *
          Settings.SYMBOLS_VISIBLE *
          SPINE_CONFIG.verticalOffset,
      };
    }
    return {
      x: config.horizontalOffset,
      y: config.verticalOffset,
    };
  }, [
    renderMode,
    symbolWidth,
    symbolHeight,
    config.horizontalOffset,
    config.verticalOffset,
  ]);

  return (
    <Container
      y={containerPosition.y}
      x={containerPosition.x}
      ref={containerRef}
      name="back_texture"
      zIndex={1}
    >
      {renderMode === "static" && staticTexture && (
        <Sprite
          texture={staticTexture}
          width={staticWidth}
          height={staticHeight}
          zIndex={2}
          anchor={0.5}
        />
      )}
    </Container>
  );
};

export default ReelBack;
